import React, { useState, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useSubscription } from 'react-apollo-hooks';
import { loader } from 'graphql.macro';
import cn from 'classnames';
import { parseISO } from 'date-fns';
import { useQuery } from 'react-apollo-hooks';

import { ProfileContext } from '@loyalup/core';
import { WithTitle, Button, Form, Modal, Table } from '@loyalup/ui';

import { useFilters } from 'components/Filters';

import { ReactComponent as CreateIcon } from 'icons/create.svg';
import { ReactComponent as SendIcon } from 'icons/send.svg';

import s from './Campaigns.module.css';

const CANCEL_CAMPAIGN_MUTATION = loader('../../gql/mutations/CancelCampaign.gql');
const CAMPAIGN_UPDATES_SUBSCRIPTION = loader('../../gql/subscriptions/campaignUpdated.gql');
const CAMPAIGNS_QUERY = loader('../../gql/queries/Campaigns.gql');

const STATUSES = {
  SCHEDULED: 'Запланирована',
  QUEUED: 'В процессе',
  IN_PROGRESS: 'В процессе',
  CANCELED: 'Отменена',
  COMPLETED: 'Выполнена',
  FAILED: 'Ошибка',
};

const TYPES = {
  PUSH: 'PUSH',
  SMS: 'SMS',
  PUSHSMS: 'PUSH/SMS',
};

const Campaigns = () => {
  const { campaignsCount, company } = useContext(ProfileContext);

  const anyCampaigns = campaignsCount > 0;

  const [t] = useTranslation();

  const [tableConfig, setTableConfig] = useState({ offset: 0, limit: 10, orderDirection: 'DESC', orderBy: 'datetime' });

  const [cancelingCampaign, setCancelingCampaign] = useState(false);

  const [cancelCampaignMutation] = useMutation(CANCEL_CAMPAIGN_MUTATION);
  const cancelCampaign = ({ id }) => {
    setCancelingCampaign(false);
    cancelCampaignMutation({ variables: { id } });
  };

  const { loading, data } = useQuery(CAMPAIGNS_QUERY, {
    suspend: false,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: tableConfig,
  });

  const allFilters = useFilters();

  const filtersData = allFilters?.allFilters.filter(({ type }) => type === 'CLIENTS') || [];

  const getFilterName = useCallback(
    name =>
      ({
        Women: t('Женщины'),
        Men: t('Мужчины'),
        'Empty phone': t('Пустой телефон'),
        'Empty email': t('Пустой е-мейл'),
      }[name] || name),
    [t],
  );

  const { data: updates } = useSubscription(CAMPAIGN_UPDATES_SUBSCRIPTION, { variables: { companyId: company.id } });

  const columns = [
    {
      name: 'datetime',
      label: t => t('Дата и время'),
      render: ({ datetime }) => t('{{datetime, datetime_short}}', { datetime: parseISO(datetime) }),
    },
    {
      name: 'type',
      label: t => t('Тип'),
      render: ({ type }) => TYPES[type],
    },
    {
      name: 'recipients',
      label: t => t('Получатели'),
      system: true,
      render: ({ clientSelection }) => {
        const filterName =
          filtersData && clientSelection.filterId
            ? filtersData.find(({ id }) => id === clientSelection.filterId)?.name
            : null;
        if (filterName) return getFilterName(filterName);
        if (clientSelection.all) return t('Все');
        return t('Некоторые');
      },
    },
    {
      name: 'status',
      label: t => t('Статус'),
      width: 180,
      render: ({ status, progressClientsCount, totalClientsCount }) => (
        <div className={cn(s.status, s[status.toLowerCase()])}>
          {t(STATUSES[status] || status)}
          {status === 'IN_PROGRESS' && (
            <span>
              &nbsp;({totalClientsCount === 0 ? 100 : Math.round((progressClientsCount / totalClientsCount) * 100)}%)
            </span>
          )}
        </div>
      ),
    },
    {
      name: 'text',
      label: t => t('Сообщение'),
      type: 'multiline',
      width: 400,
      render: ({ text }) => text,
    },
    {
      name: 'actions',
      label: t => t('Действия'),
      system: true,
      render: campaign =>
        campaign.status === 'SCHEDULED' && (
          <Button theme="black" size="sm" onClick={() => setCancelingCampaign(campaign)}>
            {t('Отменить')}
          </Button>
        ),
    },
  ];

  return (
    <WithTitle title={t('Рассылки')}>
      <div className={cn(s.header, { [s.transparentBorder]: !anyCampaigns })}>
        <h1>{t('Рассылки')}</h1>
        {anyCampaigns && (
          <Button icons="first" theme="green" element={Link} to="/campaigns/new">
            <CreateIcon />
            {t('Создать рассылку')}
          </Button>
        )}
      </div>
      {anyCampaigns ? (
        <Table
          selectable={false}
          selection={null}
          loading={loading}
          total={data && data.campaigns && data.campaigns.total}
          tableConfig={tableConfig}
          columns={columns}
          rows={(data && data.campaigns && data.campaigns.campaigns) || []}
          onChange={setTableConfig}
          highlightRow={updates && updates.campaignUpdated}
        />
      ) : (
        <div className={s.placeholderContainer}>
          <div className={s.placeholderButtons}>
            <Button theme="square" element={Link} to="/campaigns/new">
              <SendIcon />
              {t('Создать рассылку')}
            </Button>
          </div>
          <div className={s.placeholderText}>
            {t('У вас пока еще нет рассылок. Создайте свою первую Push-рассылку, нажав на кнопку.')}
          </div>
        </div>
      )}
      <Modal confirm isOpen={!!cancelingCampaign} onClose={() => setCancelingCampaign(false)}>
        <Form onSubmit={() => cancelCampaign(cancelingCampaign)}>
          <p>{t('Вы уверены что хотите отменить рассылку?')}</p>
          <div>
            <Button type="submit" theme="green" center>
              {t('Да')}
            </Button>
            <Button theme="black" onClick={() => setCancelingCampaign(false)}>
              {t('Нет')}
            </Button>
          </div>
        </Form>
      </Modal>
    </WithTitle>
  );
};

export default Campaigns;
